import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {updateCategory, updateDocs} from '../../../modules/admin/docs';
import processName from '../../../utils/processName';
import validateText from '../../../utils/validateText';
import styles from '../create/styles/create-modal.module.css';

import ButtonBox from '../ButtonBox';
import TypeBox from '../upload/TypeBox';
import ErrorBtnBox from '../../common-women/ErrorBtnBox';
import InfoBox from '../../common-women/InfoBox';
import InputExpand from '../../common-women/InputExpand';

export default function UpdateModal({ handleClose, itemToUpdate = {} }) {
    const { id, type, name, description, docsType } = itemToUpdate;

    const dispatch = useDispatch();

    const { currentId, categoryName, categoryDescription, updateCategoryResponse, updateCategoryError, updateDocsResponse, updateDocsError, updateCategoryLoading, updateDocsLoading } = useSelector(({ docs, loading }) => ({
        currentId: docs.currentId,
        categoryName: docs.categoryName,
        categoryDescription: docs.categoryDescription,
        updateCategoryResponse: docs.updateCategoryResponse,
        updateCategoryError: docs.updateCategoryError,
        updateCategoryLoading: loading['docs/UPDATE_CATEGORY'],
        updateDocsResponse: docs.updateDocsResponse,
        updateDocsError: docs.updateDocsError,
        updateDocsLoading: loading['docs/UPDATE_DOCS']
    }));

    const initialUpdateField = {
        name: name || categoryName,
        description: description ?? categoryDescription,
        nameError: false,
        descriptionError: false
    };
    
    const [updateField, setUpdateField] = useState(initialUpdateField);

    const [updateType, setUpdateType] = useState(type ?? 'CATEGORY');

    const [fileType, setFileType] = useState(docsType); // 파일 수정 시, 분류

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        const validationOptions = {
            name: {
                minLength: 2,
                maxLength: updateType === 'CATEGORY' ? 50 : 100,
                specialChars: true,
                continuousChars: true,
                emptyText: true,
                ignoreExtension: true
            },
            description: {
                minLength: 0,
                maxLength: 100,
                specialChars: false,
                continuousChars: false,
                emptyText: false,
                ignoreExtension: false
            }
        };

        const validation = validateText(value, validationOptions[name]);

        setUpdateField({
            ...updateField,
            [name]: value,
            [`${name}Error`]: !validation.isValid
        });
    };

    const changeFileType = (type) => {
        setFileType(type);
    };

    const validateUpdateBtn = () => {
        const { name, description, nameError, descriptionError } = updateField;
        
        const isChangedName = initialUpdateField.name !== name;
        const isChangedDescription = initialUpdateField.description !== description;
        const isChangedDocsType = docsType !== fileType;
        
        if (updateType === 'CATEGORY') {
            return (isChangedName || isChangedDescription) && 
                    !nameError && 
                    !descriptionError;
        }

        if (updateType === 'DOCS') {
            return (isChangedName || isChangedDocsType) && 
                    !nameError;
        }
    };
    
    const handleUpdate = () => {
        const { name, description } = updateField;

        if (updateType === 'CATEGORY') {
            const categoryId = id ?? currentId;
            dispatch(updateCategory({ id: categoryId, name: name.trim(), description: description.trim() }));
        }

        if (updateType === 'DOCS') {
            dispatch(updateDocs({ id, name: name.trim(), docsType: fileType }));
        }
    };
    
    const handleCloseModal = () => {
        setUpdateField(initialUpdateField);
        handleClose();
    };
    
    useEffect(() => {
        if ((!updateCategoryLoading && updateCategoryResponse) || (!updateDocsLoading && updateDocsResponse)) {
            handleCloseModal();
        }

        if ((!updateCategoryLoading && updateCategoryError && updateCategoryError.code === 400) || (!updateDocsLoading && updateDocsError && updateDocsError.code === 400)) {
            setUpdateField({
                ...updateField,
                nameError: true
            });
        }
    }, [updateCategoryLoading, updateCategoryResponse, updateDocsLoading, updateDocsResponse, updateCategoryError, updateDocsError]);

    return (
        <div className={styles.wrapper}>
            <p className={styles.title}>
                {updateType === 'CATEGORY' ? '카테고리' : '파일'}
                &nbsp;수정
            </p>
            <div className={styles.boxFirst}>
                <InputExpand
                    type='category-name'
                    label='이름'
                    maxLength={updateType === 'CATEGORY' ? 50 : 100}
                    placeholder='이름을 입력해 주세요'
                    name='name'
                    value={updateField.name}
                    displayValue={processName(updateField.name)} // 글자수 표시용 값
                    error={updateField.nameError}
                    handleChange={handleChange}
                />
                <InfoBox type={updateType} width='100%' />
            </div>
            {updateType === 'CATEGORY' &&
                <InputExpand
                    type='category-description'
                    label='설명'
                    maxLength='100'
                    placeholder='설명'
                    name='description'
                    value={updateField.description}
                    error={updateField.descriptionError}
                    handleChange={handleChange}
                />
            }
            {updateType === 'DOCS' &&
                <div className={styles.boxType}>
                    <p>분류</p>
                    <TypeBox
                        fileType={fileType}
                        changeFileType={changeFileType}
                    />
                </div>
            }
            <ErrorBtnBox
                fileType={updateType === 'CATEGORY' ? 'category' : 'docs'}
                requestType='update'
                error={updateCategoryError || updateDocsError}
            >
                <ButtonBox
                    text='저장'
                    inactiveColor='#CFCED4'
                    activeColor='#825DFC'
                    validateBtn={validateUpdateBtn}
                    handleCancle={handleCloseModal}
                    handleClick={handleUpdate}
                    loading={updateType === 'CATEGORY' ? updateCategoryLoading : updateDocsLoading}
                />
            </ErrorBtnBox>
        </div>
    );
}
